import type { Transaction } from './types';

// Single source of truth for linked-refund semantics. A refund link is an
// ANNOTATION, not a movimentação interna: it never removes a row from any total.
// The ONE surface that discounts a refunded expense is "Ritmo de gastos", and
// that lives entirely server-side (HomeWidgetsService) — the client never mirrors
// it. So here refund state feeds pills/drawers only; the cash-flow helpers below
// know about pre-existing neutrality (transfers, fatura payment, aportes) alone.

export type RefundState =
  | 'none'
  | 'refund'
  | 'refunded-full'
  | 'refunded-partial'
  | 'expected';

export interface RefundPillMeta {
  readonly label: string;
  /** Detail-drawer hero variant of the label. */
  readonly heroLabel: string;
  readonly tone: 'info' | 'warning';
}

export const REFUND_PILL: Record<Exclude<RefundState, 'none'>, RefundPillMeta> = {
  refund: { label: 'Reembolso', heroLabel: 'Reembolso vinculado', tone: 'info' },
  'refunded-full': { label: 'Reembolsado', heroLabel: 'Reembolso vinculado', tone: 'info' },
  'refunded-partial': { label: 'Reembolso parcial', heroLabel: 'Reembolso parcial', tone: 'info' },
  expected: { label: 'Reembolso previsto', heroLabel: 'Reembolso previsto', tone: 'warning' },
};

export function hasActiveExpectedRefund(tx: Transaction): boolean {
  return tx.expectedRefund != null && !tx.expectedRefund.expiredAt;
}

/**
 * Total covered value of a row. An expense can be refunded by SEVERAL partial estornos, so the
 * server-summed `refundedAmount` is authoritative; the per-link fallback only covers payloads
 * that predate it (and is a floor, never the whole picture).
 */
export function totalRefundedAmount(tx: Transaction): number {
  if (tx.refundedAmount != null) return tx.refundedAmount;
  if (tx.refundLinks?.length) return tx.refundLinks.reduce((sum, l) => sum + l.linkedAmount, 0);
  return tx.refundLink?.linkedAmount ?? 0;
}

/** What is still uncovered on an expense — what a further estorno could absorb. */
export function uncoveredAmount(tx: Transaction): number {
  const amount = reportingAmount(tx);
  return amount == null ? 0 : Math.max(0, Math.abs(amount) - totalRefundedAmount(tx));
}

export function getRefundState(tx: Transaction): RefundState {
  if (tx.refundLink) {
    if (tx.refundLink.role === 'refund') return 'refund';
    const amount = reportingAmount(tx);
    if (amount != null && totalRefundedAmount(tx) >= Math.abs(amount)) return 'refunded-full';
    // Partly covered AND still flagged: the flag speaks for the uncovered remainder and the
    // backend neutralizes the WHOLE expense while it holds, so it dominates the pill. Without
    // this the link masked an active flag the user could then never see or remove.
    if (hasActiveExpectedRefund(tx)) return 'expected';
    return 'refunded-partial';
  }
  if (hasActiveExpectedRefund(tx)) return 'expected';
  return 'none';
}

/**
 * Pre-existing cash-flow neutrality ONLY (transfers, fatura payment, aportes). A
 * refund link never makes a row neutral — it keeps its full weight everywhere but
 * "Ritmo de gastos".
 */
export function isFlowNeutral(tx: Transaction): boolean {
  return (tx.isCashFlowNeutral ?? tx.isInternalTransfer) === true;
}

export function hasReportingAmount(tx: Transaction): boolean {
  if (tx.reportingAmount != null) return true;
  if (tx.reportingStatus && tx.reportingStatus !== 'available') return false;
  return (tx.currencyCode ?? 'BRL').toUpperCase() === 'BRL';
}

/** Signed BRL value used by comparisons and mutations; absent means currencies are incomparable. */
export function reportingAmount(tx: Transaction): number | undefined {
  if (!hasReportingAmount(tx)) return undefined;
  return tx.reportingAmount ?? tx.amount;
}

/**
 * The amount a transaction contributes to cash-flow aggregations: 0 for a
 * pre-existing movimentação interna, its full signed amount otherwise. Refund
 * links do not enter here (they are an annotation, not a neutralization).
 */
export function countedAmount(tx: Transaction): number {
  if (isFlowNeutral(tx)) return 0;
  return reportingAmount(tx) ?? 0;
}

/**
 * Whether two magnitudes differ beyond a cent-rounding epsilon — i.e. only part of
 * the expense is covered, so only that slice leaves "Ritmo de gastos" and the
 * remainder keeps counting. Copy about a link must respect this.
 */
export function refundAmountsDiffer(amountA: number, amountB: number): boolean {
  return Math.abs(Math.abs(amountA) - Math.abs(amountB)) > 0.005;
}

/**
 * Link-success toast. A link is an annotation — totals never change; the only
 * effect is on Ritmo de gastos. A partial estorno lightens only the covered slice,
 * so the copy must not imply the whole spend leaves.
 */
export function refundLinkToastMessage(expenseAmount: number, refundAmount: number): string {
  // Only SHORT of the expense is partial. An estorno worth more than what the expense still
  // needs completes it — allocation caps at the remainder and the surplus stays income — so a
  // symmetric "differs" test would wrongly promise a leftover.
  const partial = Math.abs(refundAmount) < Math.abs(expenseAmount)
    && refundAmountsDiffer(expenseAmount, refundAmount);
  return partial
    ? 'Reembolso vinculado — parte do gasto sai do seu ritmo, o restante continua'
    : 'Reembolso vinculado — o gasto sai do seu ritmo de gastos';
}

/**
 * Detail-drawer note for a LINKED row: the pair stays in the totals; the effect is
 * on Ritmo de gastos only. A partial refund removes just the covered slice, so the
 * note tells the truth when a remainder still counts.
 */
export function refundLinkedNoteMessage(tx: Transaction): string {
  return getRefundState(tx) === 'refunded-partial'
    ? 'Vínculo de reembolso — a despesa continua nos totais; só a parte reembolsada sai do seu ritmo de gastos.'
    : 'Vínculo de reembolso — a despesa continua nos totais, mas sai do seu ritmo de gastos.';
}

/**
 * Unlink-success toast. Removing ONE link no longer necessarily returns the expense to Ritmo:
 * other estornos may still cover part of it, and an active "previsto" flag keeps the whole
 * expense out regardless. Describe the state the row lands in, not the optimistic case.
 *
 * @param removedAmount the coverage the removed link was contributing.
 */
export function refundUnlinkToastMessage(tx: Transaction, removedAmount: number): string {
  if (hasActiveExpectedRefund(tx)) {
    return 'Vínculo removido — o reembolso previsto segue valendo, então a despesa continua fora do seu ritmo de gastos.';
  }
  const stillCovered = Math.max(0, totalRefundedAmount(tx) - Math.abs(removedAmount));
  return stillCovered > 0
    ? 'Vínculo removido — a parte ainda reembolsada segue fora do seu ritmo de gastos.'
    : 'Vínculo removido — a despesa volta ao seu ritmo de gastos';
}

/** Débito/PIX flag window — past it the backend would insta-expire the flag. */
const EXPECTED_REFUND_WINDOW_DAYS = 90;
const DAY_MS = 86_400_000;

/**
 * "Reembolso previsto" is offered on unlinked, non-neutral outflows only,
 * mirroring the backend's gates exactly: a credit purchase is flaggable iff it
 * is not yet billed (`billId == null`); débito/PIX only within the 90-day
 * window after the transaction date. `now` is injectable for determinism.
 */
export function canFlagExpectedRefund(tx: Transaction, now: Date = new Date()): boolean {
  const amount = reportingAmount(tx);
  if (amount == null || amount >= 0) return false;
  // A link no longer blocks the flag — with partial estornos the user can still be expecting
  // the REMAINDER. Only a fully covered expense has nothing left to predict (mirrors the
  // backend's capacity check in FlagExpectedRefundAsync).
  if (tx.refundLink && uncoveredAmount(tx) <= 0) return false;
  if (tx.isCashFlowNeutral ?? tx.isInternalTransfer) return false;
  if (hasActiveExpectedRefund(tx)) return false;
  if (tx.paymentType === 'credit') return tx.billId == null;
  return now.getTime() - new Date(tx.date).getTime() <= EXPECTED_REFUND_WINDOW_DAYS * DAY_MS;
}
